/**
 * Single PSQ Post — Show More / Show Less toggle + one-time click tracking
 *
 * Each click swaps the truncated description for the full description
 * already present (but hidden) in the DOM, or back again — no extra
 * content fetch needed, since the post is already known to be public.
 * The button's visible label flips between "Show More" and "Show Less".
 * The accessible name is computed from that visible text plus a
 * screen-reader-only " of {post title}" suffix in the markup (WCAG 2.5.3
 * Label in Name), so this script does not set aria-label. aria-expanded
 * tracks disclosure state.
 *
 * The show-more click is only tracked the first time a post is expanded,
 * via SinglePSQPostApplicationService::recordShowMoreClick — toggling
 * back and forth afterward does not send additional requests.
 */
import { createLogger } from '@classic/Shared/Logger';

const logger = createLogger('SinglePsqPost');

const ENDPOINT = '/api/single-psq-post/record-show-more-click';
const INITIALIZED_ATTR = 'data-spp-initialized';
const TRACKED_ATTR = 'data-spp-tracked';

interface RecordShowMoreClickPayload {
    element_id: number;
}

function recordShowMoreClick(elementId: number): void {
    try {
        // The site is always derived server-side from the authenticated
        // site context, never from this payload.
        const payload: RecordShowMoreClickPayload = { element_id: elementId };
        const body = JSON.stringify(payload);

        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(ENDPOINT, blob);
            return;
        }

        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch((error: unknown) => {
            // Tracking failures never surface to the user — this is a
            // fire-and-forget analytics call — but log for debugging.
            logger.error('Failed to record show-more click', error);
        });
    } catch (e) {
        // Tracking failures never block the user from reading the rest
        // of the post, but still log for debugging.
        logger.error('Failed to record show-more click', e);
    }
}

function setButtonLabel(button: HTMLElement, expanded: boolean): void {
    const label = expanded ? 'Show Less' : 'Show More';
    const textEl = button.querySelector('.animated-button-text');
    if (textEl !== null) {
        textEl.textContent = label;
    }

    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

/**
 * @returns Whether the post is now expanded (full description visible)
 *          after this toggle.
 */
function toggle(button: HTMLElement): boolean {
    const wrapper = button.closest<HTMLElement>('.ss-spp-description-wrapper');
    if (!wrapper) {
        return false;
    }

    const truncated = wrapper.querySelector<HTMLElement>('[data-spp-description]');
    const full = wrapper.querySelector<HTMLElement>('[data-spp-full-description]');
    const expanding = !!(full && full.hidden);

    if (full) {
        full.hidden = !expanding;
    }
    if (truncated) {
        truncated.hidden = expanding;
    }

    setButtonLabel(button, expanding);

    return expanding;
}

function handleClick(event: Event): void {
    const button = event.currentTarget as HTMLElement;
    const expanded = toggle(button);

    if (!expanded || button.hasAttribute(TRACKED_ATTR)) {
        return;
    }

    const elementId = parseInt(button.getAttribute('data-spp-element-id') ?? '', 10);

    if (elementId > 0) {
        button.setAttribute(TRACKED_ATTR, '');
        recordShowMoreClick(elementId);
    }
}

function init(): void {
    const buttons = document.querySelectorAll<HTMLElement>(`[data-spp-toggle]:not([${INITIALIZED_ATTR}])`);
    buttons.forEach((button) => {
        button.setAttribute(INITIALIZED_ATTR, '');
        button.addEventListener('click', handleClick);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export {};
